import { useEffect, useMemo, useRef, useState } from "react";
import { isSelectableStore } from "../theme";
import { computeUnseenScheduleSlots, readLastViewedAt, writeLastViewedAt } from "../lib/scheduleUnseenChanges";

export function useScheduleUnseenChanges({ user, store, weekStartISO, weekDates, changeRows, loading }) {
  const [slots, setSlots] = useState([]);
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const visitRef = useRef("");
  const sawLoadingRef = useRef(false);

  const viewerKey = user?.id || user?.email || "";
  const weekDateIsos = useMemo(
    () =>
      (weekDates || [])
        .map((d) => (typeof d === "string" ? d.slice(0, 10) : ""))
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)),
    [weekDates]
  );

  useEffect(() => {
    visitRef.current = "";
    sawLoadingRef.current = false;
    setSlots([]);
    setLastViewedAt(null);
    setDismissed(false);
  }, [viewerKey, store, weekStartISO]);

  useEffect(() => {
    if (loading) sawLoadingRef.current = true;
  }, [loading]);

  useEffect(() => {
    if (!viewerKey || !isSelectableStore(store) || !weekStartISO) return;
    if (loading || !sawLoadingRef.current) return;
    const visit = `${viewerKey}:${store}:${weekStartISO}`;
    if (visitRef.current === visit) return;
    const previous = readLastViewedAt(viewerKey, store, weekStartISO);
    const unseen = computeUnseenScheduleSlots(changeRows, {
      lastViewedAt: previous,
      user,
      weekDates: weekDateIsos,
    });
    visitRef.current = visit;
    setLastViewedAt(previous);
    setSlots(unseen);
    setDismissed(false);
    writeLastViewedAt(viewerKey, store, weekStartISO);
    // Snapshot changeRows once per visit when loading flips false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerKey, store, weekStartISO, loading, user, weekDateIsos]);

  const keySet = useMemo(() => new Set((dismissed ? [] : slots).map((s) => s.key)), [dismissed, slots]);

  return {
    slots: dismissed ? [] : slots,
    lastViewedAt,
    keySet,
    dismissed,
    dismiss: () => setDismissed(true),
  };
}
